import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ShieldCheck, Lock, FileCheck2, XCircle, AlertCircle } from "lucide-react";
import { SharePointFileUpload } from "./ui/sharepoint-file-upload";
import DocumentListWithActions from "./documents/DocumentListWithActions";
import { InformationIPEIUCSection } from "./ui/information-ipe-iuc-section";
import { useWorkspaceDeficiencies } from "../hooks/useWorkspaceDeficiencies";
import { useWorkspaceMisstatements } from "../hooks/useWorkspaceMisstatements";
import { useExecutionEngagementDetails } from "../contexts/ExecutionEngagementDetailsContext";
import { applyLimitedReviewExecutionCardFilter } from "../utils/execution-limited-review-ui";

type WorkspaceArea = "Planning" | "Execution" | "ConcludingProcedures";

interface WorkspaceDeficiency {
  id: string;
  control_relevance: string;
  control_id: string[];
  deficiency_type: string;
  description: string | null;
  root_cause: string | null;
}

interface DynamicWorkspaceLandingProps {
  area: WorkspaceArea;
  workspaceName: string;
  workspaceId: string;
  onSelect: (section: string) => void;
}

interface DocEntry {
  name: string;
  url: string;
  prep_so?: any;
  rev_so?: any;
  conc_so?: any;
  eqr_so?: any;
  partner_so?: any;
}

const categories = (
  area: WorkspaceArea,
  hasDeficiencies: boolean,
  hasMisstatements: boolean,
): Array<{
  id: "romms" | "controls" | "substantive" | "deficiencies" | "misstatements";
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
}> => {
  const base: Array<{
    id: "romms" | "controls" | "substantive" | "deficiencies";
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    accent: string;
  }> = [
    {
      id: "romms",
      title: "RoMMs",
      description: "Identify and assess risks of material misstatement.",
      icon: ShieldCheck,
      accent: "from-amber-500/20 to-orange-500/20",
    },
    {
      id: "controls",
      title: "Internal Controls",
      description: "Evaluate design and implementation of controls.",
      icon: Lock,
      accent: "from-emerald-500/20 to-teal-500/20",
    },
    {
      id: "substantive",
      title: "Substantive Procedures",
      description: "Perform detailed procedures over balances and transactions.",
      icon: FileCheck2,
      accent: "from-fuchsia-500/20 to-purple-500/20",
    },
  ];

  if (hasDeficiencies) {
    base.push({
      id: "deficiencies",
      title: "Deficiencies",
      description: "View deficiencies linked to this workspace via controls.",
      icon: XCircle,
      accent: "from-red-500/20 to-rose-500/20",
    });
  }

  if (hasMisstatements) {
    base.push({
      id: "misstatements",
      title: "Misstatements",
      description: "View misstatements linked to this workspace.",
      icon: AlertCircle,
      accent: "from-orange-500/20 to-amber-500/20",
    });
  }

  return base;
};

export default function DynamicWorkspaceLanding({
  area,
  workspaceName,
  workspaceId,
  onSelect,
}: DynamicWorkspaceLandingProps) {
  const [docs, setDocs] = useState<DocEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const {
    deficiencies: workspaceDeficiencies,
    loading: defLoading,
    error: defError,
    hasDeficiencies,
  } = useWorkspaceDeficiencies(workspaceName);
  const {
    misstatements: workspaceMisstatements,
    loading: misLoading,
    hasMisstatements,
  } = useWorkspaceMisstatements(workspaceName);
  const { isLimitedReviewExecution } = useExecutionEngagementDetails();

  const sectionKeyPrefix =
    area === "Planning"
      ? "Planning"
      : area === "Execution"
        ? "Execution"
        : "ConcludingProcedures";
  const documentSectionKey = `${sectionKeyPrefix}_${workspaceId}`;

  const workspaceCards = useMemo(() => {
    const built = categories(area, hasDeficiencies, hasMisstatements);
    if (area !== "Execution" || !isLimitedReviewExecution) {
      return built;
    }
    return applyLimitedReviewExecutionCardFilter(built, true);
  }, [area, hasDeficiencies, hasMisstatements, isLimitedReviewExecution]);

  const loadDocs = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.loadDocumentListBySection?.(
        documentSectionKey,
      );
      if (res?.success) {
        setDocs(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.error || "Failed to load document list");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error while loading document list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentSectionKey]);

  return (
    <div className="space-y-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{workspaceName}</h2>
          <p className="text-sm text-white/60">
            Generic workspace for {area === "ConcludingProcedures" ? "Concluding Procedures" : area}.
          </p>
        </div>
        <Badge className="bg-white/10 text-white/80">
          {area === "ConcludingProcedures" ? "Concluding" : area}
        </Badge>
      </div>

      {/* High-level cards to describe available modules */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {workspaceCards.map((cat) => {
          const Icon = cat.icon;
          const isDeficiencyCard = cat.id === "deficiencies";
          const isMisstatementCard = cat.id === "misstatements";
          const isSubstantiveCard = cat.id === "substantive";
          return (
            <Card
              key={cat.id}
              className="group border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer"
              onClick={() => {
                if (isDeficiencyCard) {
                  onSelect(
                    `dynamic-workspace-deficiencies:${area}:${workspaceId}:${encodeURIComponent(
                      workspaceName,
                    )}`,
                  );
                } else if (isMisstatementCard) {
                  onSelect(
                    `dynamic-workspace-misstatements:${area}:${workspaceId}:${encodeURIComponent(
                      workspaceName,
                    )}`,
                  );
                } else if (isSubstantiveCard) {
                  onSelect(
                    `dynamic-workspace-substantive:${area}:${workspaceId}:${encodeURIComponent(
                      workspaceName,
                    )}`,
                  );
                } else {
                  const sectionKey =
                    cat.id === "romms"
                      ? `dynamic-workspace-romms:${area}:${workspaceId}:${encodeURIComponent(
                          workspaceName,
                        )}`
                      : `dynamic-workspace-controls:${area}:${workspaceId}:${encodeURIComponent(
                          workspaceName,
                        )}`;
                  onSelect(sectionKey);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div
                      className={`mb-3 inline-flex rounded-lg bg-gradient-to-br ${cat.accent} p-2`}
                    >
                      <Icon className="h-5 w-5 text-white/80" />
                    </div>
                    <div className="mb-1 text-sm font-semibold">{cat.title}</div>
                    <div className="mb-3 text-xs text-white/60">
                      {cat.description}
                    </div>
                  </div>
                  {(isDeficiencyCard || isMisstatementCard) && (
                    <Badge
                      variant="outline"
                      className="ml-auto text-[10px] uppercase tracking-wide"
                    >
                      {isDeficiencyCard
                        ? defLoading
                          ? "Loading"
                          : `${workspaceDeficiencies.length} item(s)`
                        : misLoading
                          ? "Loading"
                          : `${workspaceMisstatements.length} item(s)`}
                    </Badge>
                  )}
                </div>

                {!isDeficiencyCard && !isMisstatementCard && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 border-white/10 text-xs text-white/80 group-hover:bg-white/10 group-hover:text-white"
                  >
                    Continue
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Information IPE/IUC Section */}
      <InformationIPEIUCSection
        workspaceName={workspaceName}
        workspaceId={workspaceId}
        area={area}
        onFilesUploaded={loadDocs}
        onRemoveFile={loadDocs}
      />

      {/* Documents section */}
      <DocumentListWithActions
        documents={docs}
        loading={loading}
        error={error}
        onRefresh={loadDocs}
        title="Document List"
        showUploadButton={true}
        uploadSectionList={[`${documentSectionKey}_File`]}
        uploadFolderName="client"
        uploadMultiple={false}
        workspaceName={workspaceName}
        onNavigateToConfirmationLibrary={(w) => onSelect("confirmation-library:" + encodeURIComponent(w))}
        onNavigateToSamplingLibrary={(w) => onSelect("sampling-library:" + encodeURIComponent(w))}
      />
    </div>
  );
}


